// components/reports/ProductsReport.jsx
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Input } from '../../../../components/ui/input';
import { Button } from '../../../../components/ui/button';
import { CustomBarChart } from '../../components/charts/BarChart';
import { Search, Filter } from 'lucide-react';

export function ProductsReport({ data }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  if (!data) return null;

  // Get unique categories
  const categories = [...new Set(data.Table2?.map(p => p.CategoryName))];
  
  // Filter products
  const filteredProducts = data.Table2?.filter(product => {
    const matchesSearch = product.Product.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.CategoryName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || product.CategoryName === selectedCategory;
    return matchesSearch && matchesCategory && product.SalesAmount > 0;
  }) || [];

  const topProducts = filteredProducts
    .slice(0, 10)
    .map(product => ({
      name: product.Product.length > 20 
        ? product.Product.substring(0, 20) + '...' 
        : product.Product,
      value: product.SalesAmount,
      quantity: product.Quantity,
      fullName: product.Product
    }));

  const topSelling = filteredProducts
    .sort((a, b) => b.Quantity - a.Quantity)
    .slice(0, 10)
    .map(product => ({
      name: product.Product.length > 20 
        ? product.Product.substring(0, 20) + '...' 
        : product.Product,
      value: product.Quantity,
      sales: product.SalesAmount,
      fullName: product.Product
    }));

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search products..."
                  className="pl-9"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <select 
                className="border rounded-md px-3 py-2 text-sm"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="all">All Categories</option>
                {categories.map((cat, index) => (
                  <option key={index} value={cat}>{cat}</option>
                ))}
              </select>
              <Button 
                variant="outline" 
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('all');
                }}
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Top Products by Sales */}
        <CustomBarChart
          data={topProducts}
          title="Top Products by Sales"
          orientation="vertical"
          color="#f59e0b"
        />

        {/* Top Products by Quantity */}
        <CustomBarChart
          data={topSelling}
          title="Top Selling Products (Quantity)"
          orientation="vertical"
          color="#10b981"
        />
      </div>

      {/* Product Details Table */}
      <Card>
        <CardHeader>
          <CardTitle>Product Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-medium">Product</th>
                  <th className="text-left py-3 px-4 font-medium">Category</th>
                  <th className="text-left py-3 px-4 font-medium">Quantity</th>
                  <th className="text-left py-3 px-4 font-medium">Sales Amount</th>
                  <th className="text-left py-3 px-4 font-medium">Percentage</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product, index) => (
                  <tr key={index} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4">{product.Product}</td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                        {product.CategoryName}
                      </span>
                    </td>
                    <td className="py-3 px-4">{product.Quantity}</td>
                    <td className="py-3 px-4 font-medium">
                      {product.SalesAmount?.toLocaleString()}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <span>{product.Percentage}%</span>
                        <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-green-500"
                            style={{ width: `${product.Percentage}%` }}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}